import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listPublicProducts } from '../lib/api';
import { productPath } from '../utils/slug';
import type { Product } from '../../shared/types';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';
import { useCmsSections } from '../cms/useCms';
import { formatPrice } from '../lib/formatPrice';
import { usePaymentSettings } from '../lib/usePaymentSettings';
import { Loader2, ImageIcon, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ResponsiveImage from '../components/ResponsiveImage';

const ProductListPage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language as SupportedLang) || 'zh-HK';
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sections, loading: cmsLoading, error: cmsError } = useCmsSections();
  const { settings: paymentSettings } = usePaymentSettings();
  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  const cmsContent = sections.find((s) => s.key === 'product-funnel')?.contentJson || null;

  useEffect(() => {
    async function load() {
      try {
        const productsRes = await listPublicProducts({ limit: 100 });
        if (!productsRes.success || !productsRes.data) {
          throw new Error(productsRes.error?.message || 'Failed to load products');
        }
        setProducts(productsRes.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h1 className="text-3xl md:text-4xl font-serif tracking-widest text-brand-ink mb-4">
              {cmsContent ? getLocalizedText(cmsContent.title, lang, '御陽產品系列') : '御陽產品系列'}
            </h1>
            <p className="text-lg md:text-xl text-brand-ink/60 max-w-2xl mx-auto leading-relaxed">
              {cmsContent ? getLocalizedText(cmsContent.subtitle, lang, '扶陽堂精選養生保健產品，為銀髮一族及家庭而設。') : '扶陽堂精選養生保健產品，為銀髮一族及家庭而設。'}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20 text-brand-ink/50 text-lg">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              載入產品中…
            </div>
          ) : error ? (
            <div className="text-center py-16 text-brand-cinnabar text-lg bg-brand-cinnabar/5 rounded-lg">
              {error}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 text-brand-ink/50 text-lg">
              暫無產品，敬請期待。
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={productPath(product.slug)}
                  className="group bg-white border border-brand-ink/10 shadow-sm hover:shadow-lg transition-shadow overflow-hidden rounded-t-2xl"
                >
                  <div className="aspect-square bg-brand-stone/30 flex items-center justify-center overflow-hidden rounded-t-2xl">
                    {product.imageUrl ? (
                      <ResponsiveImage
                        src={product.imageUrl}
                        sizes={product.imageSizes}
                        sizesAttr="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        alt={product.nameZh || product.nameEn || ''}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <ImageIcon className="w-16 h-16 text-brand-ink/10" />
                    )}
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="text-sm text-brand-cinnabar font-medium mb-2 tracking-wide">
                      {product.category || '御陽精選'}
                    </div>
                    <h2 className="text-xl md:text-2xl font-serif text-brand-ink mb-3 leading-snug min-h-[3.5rem]">
                      {product.nameZh || product.nameEn || product.slug}
                    </h2>
                    <p className="text-base text-brand-ink/60 mb-4 line-clamp-2 leading-relaxed">
                      {product.subtitleZh || product.descriptionZh || ''}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl md:text-3xl font-bold text-brand-cinnabar">
                          {formatPrice(product.price, priceOptions)}
                        </span>
                        {product.comparePrice && product.comparePrice > product.price && (
                          <span className="text-base text-brand-ink/40 line-through">
                            {formatPrice(product.comparePrice, priceOptions)}
                          </span>
                        )}
                      </div>
                      {product.stockQuantity <= 0 ? (
                        <span className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                          暫時缺貨
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-b from-brand-ink to-brand-ink/85 text-brand-paper text-base font-medium rounded-full shadow-[0_4px_0_0_rgba(0,0,0,0.25)] group-hover:shadow-[0_6px_0_0_rgba(0,0,0,0.22)] group-hover:-translate-y-0.5 active:shadow-none active:translate-y-1 transition-all">
                          查看
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
};

export default ProductListPage;
